/**
 * AllHighlights Page
 *
 * Displays all highlights for the user in a single unified list.
 * Article groups and topic groups are interleaved by most recent
 * highlight date, with one pagination controlling everything.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageLoading from '../components/PageLoading';
import EmptyState from '../components/EmptyState';
import { getAllHighlights } from '../services/highlights';
import { formatFullDate } from '../utils/dateUtils';
import Pagination from '../components/Pagination';

const PER_PAGE = 50;

const AllHighlights = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchHighlights = useCallback(async (page, initial = false) => {
    if (initial) {
      setIsLoading(true);
    } else {
      setPageLoading(true);
    }
    try {
      const result = await getAllHighlights({ page, per_page: PER_PAGE });
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Error fetching highlights:', err);
      setError(err.message || 'Failed to load highlights');
    } finally {
      setIsLoading(false);
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHighlights(1, true);
  }, [fetchHighlights]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchHighlights(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const retryFetch = () => {
    setCurrentPage(1);
    setError(null);
    fetchHighlights(1, true);
  };

  if (isLoading) {
    return (
      <PageLoading message="Loading highlights..." />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="card-clay text-center p-8">
          <div className="text-fun-coral mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-semibold text-heading mb-2">Failed to Load Highlights</h2>
          <p className="text-secondary-text mb-4">{error}</p>
          <button
            onClick={retryFetch}
            className="btn-clay-accent"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const hasHighlights = data && data.total_highlights > 0;

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface shadow-clay border-b-3 border-clay-border">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="mr-4 p-2 text-tertiary-text hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-clay transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-heading">All Highlights</h1>
                <p className="text-sm text-secondary-text mt-1">
                  {data?.total_highlights || 0} highlights across {data?.total_articles || 0} articles
                  {(data?.total_topics || 0) > 0 && ` and ${data.total_topics} topics`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {!hasHighlights ? (
          <EmptyState
            icon={
              <svg className="w-16 h-16 text-tertiary-text mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
            }
            title="No Highlights Yet"
            description="Start highlighting important passages in your articles"
            action={{ label: 'Go to Articles', to: '/dashboard' }}
          />
        ) : (
          <>
            <div className={`space-y-6 ${pageLoading ? 'opacity-60 pointer-events-none' : ''}`}>
              {data.groups.map((group, index) => (
                group.group_type === 'article' ? (
                  <ArticleGroupCard key={`article-${group.article_id}`} group={group} />
                ) : (
                  <TopicGroupCard key={`topic-${group.topic_name}-${index}`} group={group} />
                )
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={data.total_pages}
              totalItems={data.total_groups}
              itemsPerPage={PER_PAGE}
              onPageChange={handlePageChange}
              isLoading={pageLoading}
              className="mt-6"
              itemLabel="groups"
            />
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Card component for an article highlight group.
 */
const ArticleGroupCard = ({ group }) => (
  <div className="card-clay overflow-hidden">
    {/* Article Header */}
    <div className="bg-primary-50 dark:bg-primary-950/30 px-6 py-4 border-b border-clay-border">
      <Link to={`/article/${group.article_id}`} className="group">
        <h2 className="font-display text-lg font-semibold text-heading group-hover:text-primary-600 transition-colors line-clamp-2">
          {group.article_title}
        </h2>
        <div className="flex items-center mt-2 text-sm text-secondary-text">
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <span className="truncate">{group.article_url}</span>
        </div>
      </Link>
      <div className="flex items-center mt-2">
        <span className="badge-fun-accent">
          {group.highlight_count} {group.highlight_count === 1 ? 'highlight' : 'highlights'}
        </span>
      </div>
    </div>

    {/* Highlights List */}
    <div className="divide-y divide-clay-border">
      {group.highlights.map((highlight) => (
        <div key={highlight.id} className="px-6 py-4 hover:bg-clay-bg transition-all duration-200">
          <Link to={`/article/${group.article_id}`} className="block group">
            <div className="bg-fun-lemon/20 border-l-4 border-fun-lemon px-4 py-3 rounded-r-clay mb-2">
              <p className="text-heading text-sm leading-relaxed group-hover:text-body-text">
                {highlight.exact_text}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-secondary-text">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {highlight.type === 'digest' && (
                  <>
                    <span className="text-secondary-600 font-medium">Digest</span>
                    <span>&middot;</span>
                  </>
                )}
                <time dateTime={highlight.created_at}>
                  {formatFullDate(highlight.created_at)}
                </time>
                {highlight.deleted_at && (
                  <>
                    <span>&middot;</span>
                    <span className="text-fun-peach font-medium">Outdated</span>
                  </>
                )}
              </div>
              <span className="text-primary-600 group-hover:text-primary-700 font-medium">
                View in article &rarr;
              </span>
            </div>
          </Link>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Card component for a topic highlight group (orphan digest highlights).
 */
const TopicGroupCard = ({ group }) => (
  <div className="card-clay overflow-hidden">
    {/* Topic Header */}
    <div className="bg-secondary-50 dark:bg-secondary-950/30 px-6 py-4 border-b border-clay-border">
      <Link to="/topics" className="group">
        <h3 className="font-display text-lg font-semibold text-heading group-hover:text-secondary-600 transition-colors">
          {group.topic_name}
        </h3>
        <div className="flex items-center mt-2">
          <span className="badge-fun-accent">
            {group.highlight_count} {group.highlight_count === 1 ? 'highlight' : 'highlights'}
          </span>
        </div>
      </Link>
    </div>

    {/* Digest Highlights List */}
    <div className="divide-y divide-clay-border">
      {group.highlights.map((highlight) => (
        <div key={highlight.id} className="px-6 py-4 hover:bg-clay-bg transition-all duration-200">
          <div className="bg-fun-lemon/20 border-l-4 border-fun-lemon px-4 py-3 rounded-r-clay mb-2">
            <p className="text-heading text-sm leading-relaxed">
              {highlight.exact_text}
            </p>
          </div>
          <div className="flex items-center justify-between text-xs text-secondary-text">
            <div className="flex items-center space-x-3">
              {highlight.digest_section && (
                <>
                  <span className="capitalize">
                    {highlight.digest_section === 'whats_new' && "What's New"}
                    {highlight.digest_section === 'core_theme' && 'Core Theme'}
                    {highlight.digest_section === 'recommended_reads' && 'Recommended Reads'}
                  </span>
                  <span>&middot;</span>
                </>
              )}
              <time dateTime={highlight.created_at}>
                {formatFullDate(highlight.created_at)}
              </time>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default AllHighlights;
